import { Box, Button, Text, Flex, Portal } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";
import { StyledSelect } from "@root/components/common/select/StyledSelect";
import {
  CRUD_TYPE_ENUM,
  DELETE,
  EDIT,
  PAGINATION_DEFAULTS,
  selectOptions,
  COURSE_REQUIREMENT,
} from "@root/constants";
import { DataTable, DateField } from "@root/components";
import { TableColumns } from "@root/components/kpec-admin/course-management/TableColumns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_NAMES } from "@root/constants/navigation-routes";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { ModalDelete } from "@root/components/modal-delete";
import { PlusIcon } from "@root/assets/admin/icons/plusicon";
import CustomSelect from "@root/components/common/CustomSelect";
import { useAdminDashboardTourGuide } from "@root/hooks/useAdminDashboardTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const CourseManagement = () => {
  const { t } = useTranslation("common");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const {
    coursesData,
    fetchCourses,
    deleteCourse,
    updateCourse,
    categoriesData,
    fetchCategories,
    search,
    setSearch,
    setPage: setContextPage,
    setCurrentCourse,
    setCourseData,
    methods1,
  } = useCourseManagementContext();

  // Tour guide integration
  const { adminDashboardTourGuide } = useAdminDashboardTourGuide();
  const { reset } = methods1;
  const [isDeleteModal, setisDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<string>("");

  // Atomic date range — one setState = one render = one API call.
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({ start: null, end: null });

  const dateFieldValue =
    dateRange.start && dateRange.end
      ? [dateRange.start, dateRange.end]
      : undefined;

  const clearDates = () => {
    setDateRange({ start: null, end: null });
  };

  const handleDateChange = (date: any) => {
    if (!Array.isArray(date)) return;
    const [start, end] = date;
    if (start && end) {
      setDateRange({ start, end });
    }
    // Single date: date-field.tsx handles the picker UX — no API call.
  };

  // ─── Shared payload builder ───────────────────────────────────────────────
  // Avoids duplicating the category-parsing logic across useEffect,
  // handlePageChange, and handleDeleteConfirm.
  const buildPayload = (overrides: Record<string, any> = {}) => {
    const categoryIds: Set<string> = new Set();
    const subcategoryIds: string[] = [];
    selectedCategories?.forEach((val) => {
      if (typeof val === "string") {
        if (val.includes(":")) {
          const [parentId, subId] = val.split(":");
          if (parentId) categoryIds.add(parentId);
          if (subId) subcategoryIds.push(subId);
        } else {
          categoryIds.add(val);
        }
      }
    });

    const payload: any = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: 0,
      search,
      ...overrides,
    };

    if (categoryIds.size > 0) payload.categoryId = Array.from(categoryIds).join(",");
    if (subcategoryIds.length > 0) payload.subcategoryId = subcategoryIds.join(",");
    if (selectedRequirement) payload.requirement = selectedRequirement;
    if (dateRange.start && dateRange.end) {
      payload.enrollmentPeriodStart = dateRange.start;
      payload.enrollmentPeriodEnd = dateRange.end;
    }

    return payload;
  };

  // ─── Handlers ────────────────────────────────────────────────────────────

  function handleActions(type: string, rowData: any) {
    if (type === EDIT) {
      setSelectedCourse(rowData);
      navigate(`/admin/course-management/edit/${rowData?.id}`);
    } else if (type === DELETE) {
      setisDeleteModal(true);
      setCourseToDelete(rowData.id);
    }
  }

  const handleDeleteConfirm = () => {
    if (courseToDelete) {
      deleteCourse(
        courseToDelete,
        buildPayload({ offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE })
      );
      setCourseToDelete(null);
      setisDeleteModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setCourseToDelete(null);
    setisDeleteModal(false);
  };

  function handlePageChange(newPage: number) {
    setPage(newPage);
    setContextPage(newPage);
    fetchCourses(
      buildPayload({ offset: (newPage - 1) * PAGINATION_DEFAULTS.PAGESIZE })
    );
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setContextPage(1);
      setPage(1);
    }
  };

  // ─── Filter effect ────────────────────────────────────────────────────────
  // Fires on mount (initial load) and whenever any filter changes.
  // dateRange is atomic so changing from 0→2 dates is one render = one API call.
  useEffect(() => {
    setPage(1);
    setContextPage(1);
    fetchCategories();
    fetchCourses(buildPayload());
  }, [selectedCategories, selectedRequirement, dateRange, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesOptions = categoriesData?.rows?.map((item: any) => ({
    label: item?.title,
    value: String(item?.id),
    children: item?.children?.map((subcat: any) => ({
      label: subcat?.title,
      value: String(subcat?.id),
    })),
  }));

  const requirementOptions = [
    {
      label: t("coursesPage.essentialRequired"),
      value: COURSE_REQUIREMENT.ESSENTIAL,
    },
    {
      label: t("coursesPage.optionalRecommended"),
      value: COURSE_REQUIREMENT.OPTIONAL,
    },
  ];

  return (
    <Flex direction="column" height="100%" bg="white" padding={"24px 16px"}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Box>
          <Text
            fontWeight={600}
            color={"#2F2F34"}
            fontSize={"24px"}
            mb={"10px"}
          >
            {t("courseManagement.title", "Course Management")}
          </Text>
          <Text color={"#798692"} maxWidth={"600px"}>
            {t("courseManagement.description")}
          </Text>
        </Box>
        <Button
          bgColor={"#1819B4"}
          fontWeight="medium"
          data-tour-item="admin-create-course-btn"
          onClick={() => {
            setCurrentCourse(null);
            localStorage.removeItem("currentCourse");
            setCourseData(null);
            reset();
            navigate(ROUTE_NAMES.COURSE_CREATE);
          }}
        >
          <PlusIcon
            style={{ width: "24px", height: "24px", marginRight: "8px" }}
          />
          {t("courseManagement.createCourse")}
        </Button>
      </Box>

      <Flex justifyContent={"space-between"} my={10}>
        <Box data-tour-item="admin-search-input">
          <SearchInput
            placeholder={t("coursesPage.searchPlaceholder")}
            width="500px"
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
        </Box>
        <Flex gap={4} data-tour-item="admin-filters">
          <StyledSelect
            placeholder={t("coursesPage.categories")}
            data={categoriesOptions || []}
            selectedValues={selectedCategories}
            onChange={setSelectedCategories}
            mt={10}
          />
          <Box position={"relative"} height={"fit-content"}>
            <CustomSelect
              placeholder={t("coursesPage.requirement")}
              value={selectedRequirement}
              onChange={setSelectedRequirement}
              options={requirementOptions}
              mt={5}
            />
          </Box>
          <Box position={"relative"} height={"fit-content"} width={"230px"}>
            <DateField
              height="40px"
              py={0}
              placeholder={t("translate.enrollmentPeriod")}
              value={dateFieldValue}
              onChange={handleDateChange}
              onClear={clearDates}
              disabled={false}
              isISO
              range
              inputWidth="100%"
            />
          </Box>
        </Flex>
      </Flex>

      <Box data-tour-item="admin-course-table">
        <DataTable
          columns={TableColumns(handleActions)}
          data={coursesData?.rows || []}
          showPagination={true}
          page={page}
          pageCount={coursesData?.count || 0}
          pageSize={PAGINATION_DEFAULTS.PAGESIZE}
          handlePageChange={handlePageChange}
        />
      </Box>

      <ModalDelete
        isOpen={isDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("faq.confirmDelete")}
        description={t("courses.deleteDescription")}
        deleteButtonTitle={t("faq.delete")}
        cancelButtonTitle={t("faq.cancel")}
      />

      {/* Tour Guide Components */}
      {!adminDashboardTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={adminDashboardTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}

      {adminDashboardTourGuide.isActive &&
        adminDashboardTourGuide.currentStepData && (
          <TourGuide
            isActive={adminDashboardTourGuide.isActive}
            currentStep={adminDashboardTourGuide.currentStepData}
            currentStepIndex={adminDashboardTourGuide.currentStep}
            totalSteps={adminDashboardTourGuide.totalSteps}
            onNext={adminDashboardTourGuide.nextStep}
            onBack={adminDashboardTourGuide.previousStep}
            onClose={adminDashboardTourGuide.stopTour}
            targetSelector={
              adminDashboardTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
    </Flex>
  );
};

export default CourseManagement;
